<template>
  <v-dialog v-model="display" persistent max-width="500">
    <v-card class="device-status-wrapper pa-0">
      <v-card-title class="pa-4 pl-11">
        <span>{{ $t('device.actions.update_status') }}</span>
        <v-icon @click="handleCancel">mdi-close</v-icon>
      </v-card-title>
      <v-divider dark />
      <v-card-text class="device-status-content-wrapper pa-12 pt-5">
        <v-row>
          <v-col>
            <div class="device-status-subtitle">{{ $t('device.update.modal.subtitle_new_status') }}</div>
            <v-radio-group v-model="status" hide-details class="mt-1">
              <v-radio
                v-for="option in statusOptions"
                :key="option"
                :value="option"
                :class="{ 'disabled-radio': statusDisabledMap[option] }"
                :disabled="statusDisabledMap[option]"
              >
                <template v-slot:label>
                  <span class="device-status-radio">{{ $t(`device.update.states.${option}`) }}</span>
                </template>
              </v-radio>
            </v-radio-group>
          </v-col>
        </v-row>
        <v-row v-if="status === 'lock' || status === 'ban'" class="mt-3">
          <v-col>
            <div class="device-status-subtitle">
              {{ $t('device.update.modal.subtitle_reason') }}
            </div>
            <v-radio-group v-model="reason" hide-details class="mt-1">
              <v-radio
                v-for="option in reasonsOptions"
                :key="option"
                :value="option"
                :class="{ 'disabled-radio': reasonDisabledMap[option] }"
                :disabled="reasonDisabledMap[option]"
              >
                <template v-slot:label>
                  <span class="device-status-radio">{{ $t(`device.update.states.${option}`) }}</span>
                </template>
              </v-radio>
            </v-radio-group>
          </v-col>
        </v-row>
        <v-row v-if="checkSelectedDevices > 0" class="device-status-warning">
          <v-col>
            <v-icon class="device-status-warning-icon">mdi-alert</v-icon>
            <div class="device-status-warning-message">
              {{ checkSelectedDevices }}
              {{ $tc('device.update.tolling.warning.same_status', checkSelectedDevices) }} {{ $t(`device.update.states.${status}`).toLowerCase() }}.
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider dark />
      <v-card-actions class="device-actions-wrapper py-6 px-12">
        <v-row no-gutters class="device-actions">
          <v-btn
            :disabled="disabled"
            class="ctp-btn my-0"
            dark
            @click="handleConfirm"
          >
            {{ $t('device.update.modal.confirm') }}
          </v-btn>
          <v-btn
            class="ctp-btn action-cancel my-0"
            @click="handleCancel"
          >
            {{ $t('device.update.modal.cancel') }}
          </v-btn>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script lang="ts">

import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { TollingState, TollingStateReason } from '@/store/modules/devices/types';

@Component
export default class DevicesStatusUpdate extends Vue {
  @Prop()
  private display!: boolean;

  @Prop({ required: true }) readonly userSelectedDevices!: any[];

  private statusOptions: TollingState[] = [
    TollingState.Active,
    TollingState.Ban,
    TollingState.Lock,
    TollingState.Transport
  ];

  private reasonsOptions: TollingStateReason[] = [
    TollingStateReason.FraudSuspicion,
    TollingStateReason.PaymentDefault,
    TollingStateReason.DeviceLost
  ];

  private status = '';

  private reason = '';

  private isSubmitting = false;

  private handleConfirm() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    try {
      const payload = {
        tollingStatus: this.status,
        tollingReason: this.reason
      };
      this.$emit('confirm', payload);
    } finally {
      this.isSubmitting = false;
    }
  }

  private get checkSelectedDevices() {
    return this.status ? this.userSelectedDevices
      .filter(device => device.tollingState?.state === this.status || device.personalisation?.state === this.status).length : 0;
  }

  private handleCancel() {
    this.$emit('cancel', {});
  }

  @Watch('display')
  private resetInternalData() {
    this.status = '';
    this.reason = '';
  }

  private get disabled() {
    const noReason = ((this.status === TollingState.Lock || this.status === TollingState.Ban) && !this.reason) || !this.status;
    const noVdoTransport = this.status === TollingState.Transport && this.userSelectedDevices.some(device => device.type === 'vdolink');
    return noReason || noVdoTransport || this.isSubmitting;
  }

  get statusDisabledMap(): Record<TollingState, boolean> {
    const hasVdolinkDevice = this.userSelectedDevices?.some(device => device.type === 'vdolink') ?? false;
    return {
      [TollingState.Active]: false,
      [TollingState.Ban]: hasVdolinkDevice,
      [TollingState.Transport]: hasVdolinkDevice,
      [TollingState.Lock]: false,
      [TollingState.Unpersonalised]: false,
      [TollingState.Failure]: false,
      [TollingState.Disabled]: false,
      [TollingState.Sleep]: false,
      [TollingState.Parked]: false
    };
  }

  get reasonDisabledMap(): Record<TollingStateReason, boolean> {
    return {
      [TollingStateReason.DeviceLost]: this.status === 'ban',
      [TollingStateReason.FraudSuspicion]: false,
      [TollingStateReason.PaymentDefault]: false
    };
  }
}

</script>
<style lang="scss" scoped>
@import './styles/devices-status-update.scss';
</style>
